import type { ChatMessage, CodexItem } from './index.ts';

export interface NativeAsyncQuestion { title: string; options?: string[] }
export interface AsyncQuestionAnswer { messageID: string; threadId: string; turnId: string; answers: string[] }

/** Delivery is explicit native metadata, not a phase, ID prefix or text heuristic.
 * Keep the source item untouched: async messages may legitimately carry final_answer.
 */
function asyncItem(message: Pick<ChatMessage, 'id' | 'role' | 'items'>): CodexItem | undefined {
  if (message.role !== 'assistant') return;
  const items = message.items?.filter(item => item.id === message.id);
  return items?.length === 1 && items[0].type === 'agentMessage' && items[0].delivery === 'async' ? items[0] : undefined;
}

export function isAsyncAgentMessage(message: Pick<ChatMessage, 'id' | 'role' | 'items'>): boolean {
  return !!asyncItem(message);
}

/** Invalid or oversized question metadata falls back to the original async text;
 * it never creates an answer capability or silently drops part of a question.
 */
export function asyncAgentQuestions(message: Pick<ChatMessage, 'id' | 'role' | 'items'>): NativeAsyncQuestion[] | null {
  const value = asyncItem(message)?.questions;
  if (!Array.isArray(value) || !value.length || value.length > 20) return null;
  const result: NativeAsyncQuestion[] = [];
  let size = 0;
  for (const question of value) {
    if (!question || typeof question !== 'object' || Array.isArray(question)
      || typeof question.title !== 'string' || !question.title.trim() || question.title.length > 8000) return null;
    const options = question.options;
    if (options !== undefined && (!Array.isArray(options) || !options.length || options.length > 32
      || options.some(option => typeof option !== 'string' || !option.trim() || option.length > 4000))) return null;
    size += question.title.length + (options ?? []).reduce((total: number, option: string) => total + option.length, 0);
    if (size > 32000) return null;
    result.push({ title: question.title, ...(options === undefined ? {} : { options: [...options] }) });
  }
  return result;
}

/** Only explicit, nonempty user answers are submitted; option defaults alone
 * are never consent. The model receives the source question with each answer.
 */
export function formatAsyncQuestionAnswer(message: Pick<ChatMessage, 'id' | 'role' | 'items'>, answers: unknown): string | null {
  const questions = asyncAgentQuestions(message);
  if (!questions || !Array.isArray(answers) || answers.length !== questions.length
    || answers.some(answer => typeof answer !== 'string' || !answer.trim() || answer.length > 16000)) return null;
  const text = '回答助手的问题：\n\n' + questions.map((question, index) => `${question.title}\n${answers[index].trim()}`).join('\n\n');
  return text.length <= 32000 ? text : null;
}
